/**
 * Campo de escrita.
 *
 * Detalhes que fazem diferença no uso diário:
 *   - a altura acompanha o conteúdo até um teto, para uma mensagem longa não
 *     empurrar o histórico para fora da tela;
 *   - o aviso de digitação é limitado por tempo, senão cada tecla vira um
 *     evento de rede;
 *   - arrastar arquivo para qualquer lugar da área de chat anexa.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Paperclip, SendHorizontal, Smile, X } from 'lucide-react'
import { EmojiPicker } from '@/components/chat/EmojiPicker'
import { MentionAutocomplete } from '@/components/chat/MentionAutocomplete'
import type { Attachment, GuildMember, Message } from '@nexus/shared'
import { LIMITS, formatBytes } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { api, ApiError } from '@/lib/api'
import { gateway } from '@/lib/gateway'
import {
  aplicarMencao,
  buscarMembrosParaMencao,
  detectarMencao,
  resolverMencoes,
  rotuloDeMencao,
  type MencaoEmDigitacao,
} from '@/lib/mencoes'
import { useAppStore } from '@/store/app'
import { usePreferencesStore } from '@/store/preferences'
import { useGuildMembers, useTypingUsers } from '@/hooks/selectors'
import { Avatar, Spinner, Tooltip } from '@/components/ui'

const MAX_HEIGHT = 240
const TYPING_INTERVAL_MS = 4000

/**
 * Rascunhos por canal.
 *
 * Vive fora do React porque não afeta a renderização de mais ninguém e não
 * deve sobreviver ao recarregamento da página. Sem isto, alternar para checar
 * outro canal no meio de uma frase apagaria o que já foi escrito.
 */
const drafts = new Map<string, string>()

interface PendingUpload {
  id: string
  file: File
  progress: number
  attachment?: Attachment
  error?: string
}

export function Composer({
  channelId,
  channelName,
  replyTo,
  onClearReply,
  registerHandle,
}: {
  channelId: string
  channelName: string
  replyTo: Message | null
  onClearReply: () => void
  registerHandle: (handle: { focus: () => void; insertText: (text: string) => void }) => void
}) {
  const sendMessage = useAppStore((s) => s.sendMessage)
  const sendOnEnter = usePreferencesStore((s) => s.ui.sendOnEnter)
  const guildId = useAppStore((s) => s.currentGuildId)
  const presences = useAppStore((s) => s.presences)
  const membrosDoServidor = useGuildMembers(guildId)

  const [value, setValue] = useState(() => drafts.get(channelId) ?? '')
  const [uploads, setUploads] = useState<PendingUpload[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [emojisAbertos, setEmojisAbertos] = useState(false)

  const [mencao, setMencao] = useState<MencaoEmDigitacao | null>(null)
  const [mencaoAtiva, setMencaoAtiva] = useState(0)

  const sugestoes = useMemo(
    () => (mencao ? buscarMembrosParaMencao(membrosDoServidor, mencao.termo) : []),
    [mencao, membrosDoServidor],
  )

  /**
   * Reavalia se o cursor está dentro de uma menção.
   *
   * Precisa rodar também quando só o cursor se move: clicar no meio de um
   * `@nom` já escrito deve reabrir a lista para terminar o nome.
   */
  const revisarMencao = useCallback((texto: string, cursor: number) => {
    const encontrada = detectarMencao(texto, cursor)
    setMencao(encontrada)
    setMencaoAtiva(0)
  }, [])

  /*
    Quem já foi apontado nesta mensagem.

    O texto guarda só o nome, e dois membros do mesmo servidor podem ter o
    mesmo apelido. Este registro é o que diz qual dos dois foi escolhido na
    lista; sem ele, a menção iria para quem aparecesse primeiro.
  */
  const apontados = useRef(new Map<string, string>())

  const escolherMencao = (membro: GuildMember) => {
    const campo = textareaRef.current
    if (!mencao || !campo) return

    const nome = rotuloDeMencao(membro)
    apontados.current.set(nome, membro.userId)

    const resultado = aplicarMencao(value, mencao, campo.selectionStart, nome)
    setValue(resultado.texto)
    drafts.set(channelId, resultado.texto)
    setMencao(null)

    requestAnimationFrame(() => {
      campo.focus()
      campo.setSelectionRange(resultado.cursor, resultado.cursor)
    })
  }

  /**
   * Insere o emoji onde o cursor está, e não no fim do texto.
   *
   * Quem escreveu uma frase inteira e volta para acrescentar um emoji no meio
   * espera que ele apareça ali. O cursor fica logo depois do que foi inserido,
   * para dar para continuar digitando sem tirar a mão do teclado.
   */
  const inserirEmoji = (emoji: string) => {
    const campo = textareaRef.current
    const inicio = campo?.selectionStart ?? value.length
    const fim = campo?.selectionEnd ?? value.length

    const proximo = value.slice(0, inicio) + emoji + value.slice(fim)
    setValue(proximo)
    drafts.set(channelId, proximo)

    requestAnimationFrame(() => {
      const posicao = inicio + emoji.length
      campo?.focus()
      campo?.setSelectionRange(posicao, posicao)
    })
  }
  const fileInputRef = useRef<HTMLInputElement>(null)
  const lastTypingSentAt = useRef(0)
  const previousChannelId = useRef(channelId)

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`
  }, [])

  useEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])

  useEffect(() => {
    registerHandle({
      focus: () => textareaRef.current?.focus(),
      insertText: (text) => {
        setValue((current) => {
          const next = current + text
          drafts.set(channelId, next)
          return next
        })
        textareaRef.current?.focus()
      },
    })
  }, [registerHandle, channelId])

  // Ao trocar de canal, guarda o texto do anterior e restaura o do novo. Os
  // anexos não seguem junto: eles já foram enviados vinculados a este canal.
  useEffect(() => {
    if (previousChannelId.current !== channelId) {
      const pending = drafts.get(previousChannelId.current)
      if (pending !== undefined && pending.trim().length === 0) {
        drafts.delete(previousChannelId.current)
      }
      previousChannelId.current = channelId
      setValue(drafts.get(channelId) ?? '')
      setUploads([])
      setError(null)
    }
    textareaRef.current?.focus()
  }, [channelId])

  useEffect(() => {
    if (replyTo) textareaRef.current?.focus()
  }, [replyTo])

  const notifyTyping = useCallback(() => {
    const now = Date.now()
    if (now - lastTypingSentAt.current < TYPING_INTERVAL_MS) return
    lastTypingSentAt.current = now
    gateway.send({ op: 'typing', d: { channelId } })
  }, [channelId])

  const uploadFiles = useCallback(
    async (files: File[]) => {
      const accepted = files.slice(0, LIMITS.attachmentsPerMessage - uploads.length)

      for (const file of accepted) {
        if (file.size > LIMITS.attachmentSizeBytes) {
          setError(
            `"${file.name}" tem ${formatBytes(file.size)}. O limite é ${formatBytes(LIMITS.attachmentSizeBytes)}.`,
          )
          continue
        }

        const id = `${Date.now()}-${file.name}`
        setUploads((prev) => [...prev, { id, file, progress: 0 }])

        try {
          const attachment = await api.messages.uploadAttachment(channelId, file, (progress) => {
            setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, progress } : u)))
          })
          setUploads((prev) =>
            prev.map((u) => (u.id === id ? { ...u, attachment, progress: 100 } : u)),
          )
        } catch (err) {
          const message = err instanceof ApiError ? err.message : 'Falha ao enviar'
          setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, error: message } : u)))
        }
      }
    },
    [channelId, uploads.length],
  )

  const handleSend = async () => {
    const content = value.trim()
    const attachmentIds = uploads
      .map((u) => u.attachment?.id)
      .filter((id): id is string => Boolean(id))

    if (content.length === 0 && attachmentIds.length === 0) return
    if (uploads.some((u) => !u.attachment && !u.error)) return

    setSending(true)
    setError(null)

    // Limpa o campo antes da resposta do servidor: a percepção de instantâneo
    // vale mais do que a garantia de que o texto sobreviveria a uma falha rara.
    setValue('')
    drafts.delete(channelId)
    setUploads([])
    setMencao(null)
    apontados.current.clear()
    onClearReply()

    try {
      await sendMessage(
        channelId,
        resolverMencoes(content, membrosDoServidor, apontados.current),
        replyTo?.id ?? null,
        attachmentIds,
      )
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Não foi possível enviar'
      setError(message)
      // Devolve o texto para o usuário não perder o que escreveu.
      setValue(content)
      drafts.set(channelId, content)
    } finally {
      setSending(false)
      textareaRef.current?.focus()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Com a lista de menções aberta, as setas e o Enter pertencem a ela. Sem
    // isso, escolher alguém exigiria tirar a mão do teclado.
    if (sugestoes.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setMencaoAtiva((atual) => (atual + 1) % sugestoes.length)
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setMencaoAtiva((atual) => (atual - 1 + sugestoes.length) % sugestoes.length)
        return
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        const escolhido = sugestoes[mencaoAtiva]
        if (escolhido) {
          event.preventDefault()
          escolherMencao(escolhido)
          return
        }
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        setMencao(null)
        return
      }
    }

    const isSendCombo = sendOnEnter
      ? event.key === 'Enter' && !event.shiftKey
      : event.key === 'Enter' && (event.ctrlKey || event.metaKey)

    if (isSendCombo) {
      event.preventDefault()
      void handleSend()
      return
    }

    if (event.key === 'Escape' && replyTo) {
      event.preventDefault()
      onClearReply()
    }
  }

  const busy = sending || uploads.some((u) => !u.attachment && !u.error)

  return (
    <div
      className="relative shrink-0 px-4 pb-5"
      onDragOver={(e) => {
        e.preventDefault()
        setDragging(true)
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return
        setDragging(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        void uploadFiles(Array.from(e.dataTransfer.files))
      }}
    >
      <MentionAutocomplete
        membros={sugestoes}
        ativo={mencaoAtiva}
        presencas={presences}
        onEscolher={escolherMencao}
        onPassarPorCima={setMencaoAtiva}
      />

      {replyTo ? (
        <div className="flex items-center gap-2 rounded-t-lg bg-surface-overlay px-3 py-1.5 text-[13px]">
          <span className="text-content-muted">Respondendo a</span>
          <Avatar
            userId={replyTo.authorId}
            name={replyTo.author.displayName}
            src={replyTo.author.avatarUrl}
            size="xs"
          />
          <span className="font-medium text-content-primary">{replyTo.author.displayName}</span>
          <span className="min-w-0 flex-1 truncate text-content-muted">{replyTo.content}</span>
          <button
            type="button"
            onClick={onClearReply}
            aria-label="Cancelar resposta"
            className="rounded p-0.5 text-content-muted transition-colors hover:text-content-primary"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : null}

      {uploads.length > 0 ? (
        <div className="flex flex-wrap gap-2 rounded-t-lg bg-surface-overlay p-2">
          {uploads.map((upload) => (
            <div
              key={upload.id}
              className="relative flex w-40 flex-col gap-1 rounded-md bg-surface-raised p-2"
            >
              <div className="flex items-center gap-1.5">
                <span className="min-w-0 flex-1 truncate text-xs text-content-primary">
                  {upload.file.name}
                </span>
                <button
                  type="button"
                  onClick={() => setUploads((prev) => prev.filter((u) => u.id !== upload.id))}
                  aria-label="Remover anexo"
                  className="rounded p-0.5 text-content-muted hover:text-danger"
                >
                  <X className="size-3.5" />
                </button>
              </div>

              {upload.error ? (
                <span className="text-[10px] text-danger">{upload.error}</span>
              ) : upload.attachment ? (
                <span className="text-[10px] text-success">
                  {formatBytes(upload.file.size)}
                </span>
              ) : (
                <div className="h-1 overflow-hidden rounded-full bg-surface-active">
                  <div
                    className="h-full bg-brand transition-[width] duration-150"
                    style={{ width: `${upload.progress}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}

      <div
        className={cn(
          'flex items-end gap-2 bg-surface-raised px-3 py-2.5 transition-colors',
          replyTo || uploads.length > 0 ? 'rounded-b-lg' : 'rounded-lg',
          dragging && 'ring-2 ring-brand ring-inset',
        )}
      >
        <Tooltip content="Anexar arquivo">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Anexar arquivo"
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary"
          >
            <Paperclip className="size-5" />
          </button>
        </Tooltip>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            void uploadFiles(Array.from(e.target.files ?? []))
            e.target.value = ''
          }}
        />

        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            drafts.set(channelId, e.target.value)
            revisarMencao(e.target.value, e.target.selectionStart)
            if (e.target.value.length > 0) notifyTyping()
          }}
          onSelect={(e) => {
            const campo = e.currentTarget
            revisarMencao(campo.value, campo.selectionStart)
          }}
          onBlur={() => setMencao(null)}
          onKeyDown={handleKeyDown}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData.files)
            if (files.length > 0) {
              e.preventDefault()
              void uploadFiles(files)
            }
          }}
          rows={1}
          maxLength={LIMITS.messageContent.max}
          placeholder={`Conversar em #${channelName}`}
          className="max-h-60 min-h-6 flex-1 resize-none bg-transparent py-1 text-[15px] outline-none placeholder:text-content-muted"
        />

        <div className="relative shrink-0">
          <Tooltip content="Emoji">
            <button
              type="button"
              onClick={() => setEmojisAbertos((v) => !v)}
              aria-label="Inserir emoji"
              aria-expanded={emojisAbertos}
              className={cn(
                'flex size-8 items-center justify-center rounded-md transition-colors',
                emojisAbertos
                  ? 'bg-surface-active text-content-primary'
                  : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary',
              )}
            >
              <Smile className="size-5" />
            </button>
          </Tooltip>

          {emojisAbertos ? (
            <EmojiPicker onEscolher={inserirEmoji} onFechar={() => setEmojisAbertos(false)} />
          ) : null}
        </div>

        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={busy || (value.trim().length === 0 && uploads.length === 0)}
          aria-label="Enviar mensagem"
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-md transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-30',
            'bg-brand text-white hover:bg-brand-hover',
          )}
        >
          {busy ? <Spinner className="size-4" /> : <SendHorizontal className="size-4" />}
        </button>
      </div>

      <div className="mt-1 flex items-center justify-between px-1">
        {error ? (
          <span className="text-xs text-danger">{error}</span>
        ) : (
          <TypingIndicator channelId={channelId} />
        )}

        {value.length > LIMITS.messageContent.max - 200 ? (
          <span className="text-xs text-content-muted tabular-nums">
            {LIMITS.messageContent.max - value.length}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function TypingIndicator({ channelId }: { channelId: string }) {
  const members = useAppStore((s) => s.members)
  const currentGuildId = useAppStore((s) => s.currentGuildId)

  // O estado de digitação expira por tempo, então precisamos reavaliar mesmo
  // sem nenhum evento novo chegando.
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), 1000)
    return () => clearInterval(timer)
  }, [])

  const userIds = useTypingUsers(channelId, tick)
  if (userIds.length === 0) return <span />

  const names = userIds
    .map((id) => {
      const member = currentGuildId ? members[`${currentGuildId}:${id}`] : undefined
      return member?.nickname ?? member?.user.displayName ?? 'Alguém'
    })
    .slice(0, 3)

  const text =
    userIds.length > 3
      ? 'Várias pessoas estão digitando'
      : names.length === 1
        ? `${names[0]} está digitando`
        : `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]} estão digitando`

  return (
    <span className="flex items-center gap-1.5 text-xs text-content-secondary">
      <span className="flex gap-0.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="size-1 animate-bounce rounded-full bg-content-muted"
            style={{ animationDelay: `${i * 120}ms` }}
          />
        ))}
      </span>
      {text}
    </span>
  )
}
